import { useRouter, useSearchParams } from "next/navigation";
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  createRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";

import useResetStateTravelerEdit from "@/app/common-hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { Traveller } from "@/app/common-types/quote/question-payload-type";
import { ScrollRefItem } from "@/app/layout/question/question-layout.hook";
import { updateOrganiserPMI } from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { hasPassedOrganiser } from "@/utils/session.utils";
import { generateOrganiserForPMI, calculateAge } from "@/utils/traveller.utils";

import {
  PrivateMedicalInsuranceCmsDataType,
  GroupedCmsDataSingleItemType,
} from "../types/private-medical-insurance.type";
import { groupedCoverForCmsData } from "../utils/private-medical-insurance-utils";

export interface OrganiserPMIDetail {
  firstName: string;
  lastName: string;
  age: number;
  hasPMI?: boolean;
}

// Extend the context type
const CoverForContext = createContext<{
  cmsData: PrivateMedicalInsuranceCmsDataType;
  selectedStateCmsData: GroupedCmsDataSingleItemType;
  isDrawerOpen: boolean;
  isOpenUnsavedChangesModal: boolean;
  pmiDetails: Traveller[];
  organiserPMI?: OrganiserPMIDetail;
  showOrganiserPMI: boolean;
  isTravelerEditOrOrganizerEdit: boolean;
  updateOrganiserPMI: (hasPMI: boolean) => void;
  handleUnSavedWarningMessageContinueBtn: () => void;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setIsOpenUnsavedChangesModal: Dispatch<SetStateAction<boolean>>;
  setPmiDetails: Dispatch<SetStateAction<Traveller[]>>;
  scrollRefArray: ScrollRefItem[];
  organiserPMIRef: React.RefObject<HTMLDivElement | null>;
  pmiRefs: React.RefObject<HTMLDivElement | null>[];
  pmiExperimentEnable?: boolean;
} | null>(null);

// Provider component
function PMIProvider({
  children,
  initialValue,
}: Readonly<{
  children: ReactNode;
  initialValue: PrivateMedicalInsuranceCmsDataType;
}>) {
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { travellerDetails } = attributes;
  const dispatch = useDispatch();
  const searchParams = useSearchParams();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState<boolean>(false);
  const [pmiDetails, setPmiDetails] = useState<Traveller[]>([]);

  // Dynamic refs for each PMI question
  const pmiRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([]);
  const organiserPMIRef = useRef<HTMLDivElement>(null);
  const [pmiExperimentEnable, setPmiExperimentEnable] =
    useState<boolean>(false);

  const router = useRouter();
  const { resetQuestionData } = useResetStateTravelerEdit();

  useEffect(() => {
    if (sessionStorage?.getItem("pmi_experiment_enable") === "true") {
      setPmiExperimentEnable(true);
    }
  }, []);

  // Extract isTravelerEditOrOrganizerEdit as a separate memoized value
  const isTravelerEditOrOrganizerEdit = useMemo(() => {
    return Boolean(
      searchParams.get("organizerEdit") || searchParams.get("travellerEdit")
    );
  }, [searchParams]);
  // Check conditions for showing organiser PMI
  const showOrganiserPMI = useMemo(() => {
    const hasPassedOrganiserPage = hasPassedOrganiser();
    const organiserTravelling = Boolean(attributes?.organiser?.travelling);

    return (
      hasPassedOrganiserPage &&
      isTravelerEditOrOrganizerEdit &&
      organiserTravelling
    );
  }, [isTravelerEditOrOrganizerEdit, attributes?.organiser?.travelling]);

  // Generate organiser PMI object
  const organiserPMI: OrganiserPMIDetail | undefined = useMemo(() => {
    if (!showOrganiserPMI || !attributes?.organiser) return undefined;

    let organiserData = generateOrganiserForPMI(attributes.organiser);

    if (!organiserData) return undefined;

    return {
      firstName: organiserData.firstName,
      lastName: organiserData.lastName,
      age: organiserData.age,
      hasPMI: organiserData.hasPMI,
    };
  }, [
    showOrganiserPMI,
    attributes.organiser,
    searchParams,
    travellerDetails?.traveller,
  ]);

  // Update organiser PMI handler
  const updateOrganiserPMIHandler = useCallback(
    (hasPMI: boolean) => {
      dispatch(updateOrganiserPMI({ hasPMI }));
    },
    [dispatch]
  );

  // Create scrollRefArray for useQuestionLayoutHook
  const scrollRefArray = useMemo(() => {
    // Create refs dynamically based on pmiDetails length
    pmiRefs.current = pmiDetails.map(() => createRef<HTMLDivElement>());

    const refs: ScrollRefItem[] = [];

    // Add organiser PMI ref if applicable
    if (showOrganiserPMI) {
      refs.push({
        ref: organiserPMIRef,
        isToggleWrapper: true,
      });
    }

    // Add dynamic traveller PMI refs
    pmiRefs.current.forEach((ref) => {
      refs.push({
        ref,
        isToggleWrapper: true,
      });
    });

    return refs;
  }, [pmiDetails?.length, showOrganiserPMI]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPmiDetails(() => {
      return ((travellerDetails?.traveller as Traveller[]) ?? [])
        .slice()
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        .map((traveller) => {
          let calculatedAge = traveller.age;
          if (!calculatedAge && traveller.dateOfBirth) {
            calculatedAge = calculateAge(traveller.dateOfBirth);
          }
          return {
            ...traveller,
            age: calculatedAge || 0,
          };
        });
    });
  }, [travellerDetails?.traveller]);

  const selectedStateCmsData = useMemo(() => {
    const filledState = pmiDetails.every((item) => item?.hasPMI !== undefined)
      ? "COMPLETED"
      : "NOT_COMPLETED";
    const groupedCmsData = groupedCoverForCmsData(
      initialValue,
      pmiExperimentEnable
    );
    return groupedCmsData[filledState];
  }, [initialValue, pmiDetails, pmiExperimentEnable]);

  const handleUnSavedWarningMessageContinueBtn = useCallback(() => {
    setIsOpenUnsavedChangesModal(false);
    resetQuestionData();
    router.push("/");
  }, [resetQuestionData, router]);

  const contextValue = useMemo(
    () => ({
      cmsData: initialValue,
      selectedStateCmsData,
      isDrawerOpen,
      isOpenUnsavedChangesModal,
      pmiDetails,
      pmiExperimentEnable,
      organiserPMI,
      showOrganiserPMI,
      isTravelerEditOrOrganizerEdit,
      updateOrganiserPMI: updateOrganiserPMIHandler,
      handleUnSavedWarningMessageContinueBtn,
      setIsDrawerOpen,
      setIsOpenUnsavedChangesModal,
      setPmiDetails,
      scrollRefArray,
      organiserPMIRef,
      pmiRefs: pmiRefs.current,
    }),
    [
      initialValue,
      isDrawerOpen,
      selectedStateCmsData,
      isOpenUnsavedChangesModal,
      pmiDetails,
      pmiExperimentEnable,
      organiserPMI,
      showOrganiserPMI,
      isTravelerEditOrOrganizerEdit,
      updateOrganiserPMIHandler,
      handleUnSavedWarningMessageContinueBtn,
      scrollRefArray,
    ]
  );

  return (
    <CoverForContext.Provider value={contextValue}>
      {children}
    </CoverForContext.Provider>
  );
}

// Custom hook to use the context
export const usePMIContext = () => {
  const context = useContext(CoverForContext);
  if (!context) {
    throw new Error("usePMIContext must be used within a PMIProvider");
  }
  return context;
};

export default PMIProvider;
